import { useEffect, useRef, useState } from 'react'
import { Ausgezeichnet } from '../../components/Ausgezeichnet'
import { useTexte } from '../../texte'

import { useSendMessage, useSettings, useTagestrenner, useTagestrennerAendern, useTrip } from '../../hooks/queries'
import { merken } from '../../lib/outbox'
import { sendeOrt, useSendKey } from '../../hooks/useSendKey'
import { useOutbox } from '../../hooks/useOutbox'
import { unterwegs, useAttachments } from '../../hooks/useAttachments'
import { useRecording } from '../../hooks/useRecording'
import { Musikwahl } from './Musikwahl'
import { Kartenlinkwahl } from './Kartenlinkwahl'
import { usePlusMenu } from './usePlusMenu'
import type { Meldezeile } from './useMeldezeile'
import { Restzeichen } from '../../components/Restzeichen'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'
import { api, ApiError, fehlertext, netzStand } from '../../api/client'
import type { DayCardPreview, Message, TagestrennerAngaben } from '../../api/types'
import { dauerText, standbildAdresse } from '../../lib/video'
import { Standbild } from '../../components/Standbild'
import { groesse, medienwort } from '../../lib/format'
import { bildschirmtastaturVerdeckt } from '../../lib/tastatur'
import { useTastaturOffen } from '../../hooks/useTastatur'
import { notiere } from '../../lib/sendeprotokoll'
import { heuteSchluessel } from '../../lib/ortszeit'
import { insertionPlace, type Insertion } from '../../lib/insertion'
import {
  GifIcon,
  GruppeIcon,
  PhotoIcon,
  PlusIcon,
  PersonIcon,
  PfeilHochIcon,
  VideoIcon,
  WelleIcon,
  XIcon,
} from '../../components/Icons'
import { Anhangwahl, wegeZaehlen } from './Anhangwahl'
import { Tagwahl, TagwahlWarten } from './Tagwahl'
import { RouteFilmSheet } from './RouteFilmSheet'
import { canAnimate } from './routeFilm'
import { animationFrom } from '../settings/AnimationFields'
import { Tagestrennerwahl } from './Tagestrennerwahl'
import { Wetterwahl } from './Wetterwahl'
import { Abstimmungsformular } from './Abstimmungsformular'
import { Auszeichnungsknoepfe } from '../../components/Auszeichnungsknoepfe'
import { lese, schreibe } from '../../lib/reichtext'
import { useSchreibfeld } from '../../hooks/useSchreibfeld'
import {
  eingabeHolen,
  eingabeMerken,
  eingabeSchluessel,
  type Attachment,
} from '../../lib/eingabevorrat'
import { kannAufnehmen, type Aufnahme } from '../../lib/sprachaufnahme'
import { Aufnahmeleiste } from './Aufnahmeleiste'
import { Gifwahl, useGifsAktiv } from './Gifwahl'
import { Fotowahl } from '../map/Fotowahl'
import type { Bezug } from './ThreadPage'

interface Props {
  tripId: number
  /**
   * The one message line (1.80.0), from the chat page (1.117.4): the
   * bubbles' menus report to it as well, and it must outlive this input.
   */
  meldezeile: Meldezeile
  /**
   * Der Hinweis im leeren Feld — **von lang nach kurz**.
   *
   * Auf einem schmalen Telefon passt „Nachricht an alle Empfänger" nicht in
   * die Zeile und wurde abgeschnitten. Statt eines Auslassungszeichens
   * mitten im Wort steht hier eine Staffel: der Composer nimmt die längste
   * Fassung, die wirklich hineinpasst. Der erste Eintrag ist zugleich das,
   * was Vorlesehilfen bekommen — dort zählt die Breite nicht.
   */
  /**
   * Die Staffel der Platzhalter, von lang nach kurz.
   *
   * **`readonly`, seit der Wortlaut in `texte/de.ts` steht**: die Datei ist
   * `as const`, und was von dort kommt, ist unveränderlich. Gelesen wird es
   * hier ohnehin nur.
   */
  platzhalter: readonly string[]
  /**
   * Wer es sehen wird — daran hängt das Sinnbild im leeren Feld.
   *
   * **Abgeleitet, nicht aufgezählt:** ein Empfänger oder alle. Damit deckt
   * eine Angabe alle drei Fälle ab — privater Verlauf, Beitrag des Autors
   * an alle, Antwort eines Mitlesenden (die sieht nur der Autor).
   *
   * Sie steht neben `platzhalter`, weil beides dieselbe Aussage macht: das
   * eine in Worten, das andere als Bild. Wer eines ändert, sieht das andere.
   */
  reichweite: 'einer' | 'alle'
  disabled?: boolean
  /**
   * Der geöffnete Antwortverlauf. Gesetzt heißt: was hier geschrieben wird,
   * geht an genau diesen Menschen — der Server macht daraus eine private
   * Nachricht.
   */
  withUser?: number
  /**
   * Was statt der Eingabezeile dasteht, wenn nicht geschrieben werden kann.
   *
   * Ohne Angabe: die abgeschlossene Reise, der ursprüngliche Fall. Der Grund
   * gehört an die Stelle, an der sonst das Schreibfeld wäre — ein Feld, das
   * nichts kann, verspricht etwas, das es nicht hält.
   */
  disabledHint?: React.ReactNode
  /**
   * Worauf sich die Nachricht bezieht — gesetzt an einer Sprechblase weiter
   * oben oder an einem Foto in der Großansicht.
   */
  bezug?: Bezug | null
  /** Den Bezug wieder loswerden: das ✕ am Zitat, und nach dem Senden. */
  onBezugWeg?: () => void
  /**
   * Etwas ist raus -- der Verlauf soll ans Ende springen.
   *
   * **Warum das jemand anders macht:** die Zeitleiste gehört der Seite, nicht
   * der Eingabezeile. Hier weiß nur diese Stelle, *wann* etwas hinausgeht --
   * und dort weiß nur die Seite, *wohin* gesprungen wird.
   */
  onGesendet?: () => void
  /** The draft chat (#85): set, this line writes drafts. */
  entwurf?: boolean
  /**
   * Before or after a chosen post (#130): the marker above the field with
   * its words and an X, and where every road of this line sends the post.
   * `onDone` gets the new post's id -- the page jumps there.
   */
  insertion?: (Insertion & { text: string; onDrop: () => void; onDone: (messageId: number) => void }) | null
  /** A day card to compute anew (1.86.0) -- the sheet and the drawing live in the plus menu. */
  tagAenderung?: Message | null
  onTagAenderungFertig?: () => void
}

// `Stufe` and `Attachment` live in `lib/eingabevorrat.ts` since 1.22.0: the
// store that keeps unsent input per place needs to know them as well.

/**
 * Das Vorschaubild zum Bezug -- aus den Daten des offenen Chats.
 *
 * Hier ausnahmsweise aus dem Zwischenspeicher und nicht vom Server: das Zitat
 * über der Eingabezeile sieht **nur der, der gerade schreibt**, und zwar zu
 * einer Nachricht, die vor ihm im selben Verlauf steht. In der Sprechblase
 * einer gesendeten Antwort ist es umgekehrt -- dort liefert der Server den
 * Auszug, denn dort lesen andere mit.
 */
function bezugBild(bezug: Bezug): string | null {
  const medium = bezugMedium(bezug)
  // **Eine Sprachnachricht hat kein Bild.** Ohne diese Zeile stünde ihre
  // Tondatei als Bildadresse im Zitat -- ein kaputtes Bildchen.
  if (medium === undefined || medium.kind === 'audio') return null
  /*
   * **Das Vorschaubild und nicht das Original** (Codereview D5).
   *
   * Hier stand `poster_url ?? url`, obwohl `thumb_url` danebenliegt: für einen
   * Kasten von vierzig Punkten lud die Zeile das ganze Foto. Und für ein Video
   * ohne Standbild fiel sie auf `url` zurück -- also auf die **Videodatei** als
   * Bildadresse, und das ist derselbe Fehler, den die Zeile darüber für den
   * Ton behebt.
   *
   * `standbildAdresse()` kennt die Regel und steht an einer Stelle.
   */
  return standbildAdresse(medium) ?? null
}

/** Das gemeinte Medium — an einer Stelle gesucht, dreimal gebraucht. */
function bezugMedium(bezug: Bezug) {
  return bezug.mediaId === undefined
    ? undefined
    : bezug.message.media.find((m) => m.id === bezug.mediaId)
}

/**
 * Was das Sinnbild im leeren Feld an Breite kostet — 16 Punkte plus 6 Abstand.
 *
 * Als Zahl und nicht gemessen: sie steht in `app.css` an `.composer__leer`
 * genauso, und beides zu messen hieße, auf ein Bauteil zu warten, das es im
 * Augenblick der Messung noch gar nicht gibt.
 */
const SINNBILD_PLATZ = 22

/** Die Eingabezeile: wächst mit dem Text, Enter sendet, Umschalt+Enter bricht um. */
export function Composer({
  tripId,
  meldezeile,
  platzhalter,
  reichweite,
  disabled,
  withUser,
  disabledHint,
  bezug,
  onBezugWeg,
  onGesendet,
  entwurf = false,
  insertion = null,
  tagAenderung = null,
  onTagAenderungFertig,
}: Props) {
  const t = useTexte()
  const [text, setText] = useState('')
  const grenzen = useTextgrenzen()
  const [sendeWennFertig, setSendeWennFertig] = useState(false)
  /*
   * **Das Schreibfeld und alles, wo es gelesen wird, kommt aus einer Stelle**
   * (1.69.0) -- dieselbe nimmt das Blatt zum Ändern. Was den Composer
   * unterscheidet, bleibt hier: er wächst mit seinem Text (`grow()`), sendet
   * auf Eingabe und trägt einen Platzhalter.
   */
  const schreibfeld = useSchreibfeld((wortlaut) => {
    setText(wortlaut)
    grow()
  })
  const field = schreibfeld.feld
  const hatAuswahl = schreibfeld.hatAuswahl
  const picker = useRef<HTMLInputElement>(null)
  const send = useSendMessage(tripId, withUser, entwurf)
  const sendeSchluessel = useSendKey(sendeOrt(tripId, withUser, entwurf))

  /**
   * Was für **diesen** Verlauf noch im Ausgangskorb wartet.
   *
   * Gebraucht wird es für eine Frage beim Senden, und die Antwort darauf steht
   * unten in `submit()`: wer schreibt, während die Schlange noch abgearbeitet
   * wird, darf sie nicht überholen.
   */
  const wartend = useOutbox(tripId, withUser, entwurf)
  // **Eine Abfrage, drei Auskünfte** (1.23.0). Sie liegt im
  // Zwischenspeicher, teuer war das Dreifache also nicht -- aber dreimal
  // dieselbe Zeile ist dreimal die Gelegenheit, sie beim nächsten Umbau
  // verschieden zu schreiben.
  const reise = useTrip(tripId).data
  /** Die Uhr der Reise. */
  const reiseVersatz = reise?.zones ?? null
  /**
   * **Der Autor erzählt, die anderen antworten.**
   *
   * Fotos und Videos sind der Bericht: sie kosten Kontingent und stehen
   * dauerhaft in der Reise. Eine Antwort trägt Worte — und ein GIF, weil das
   * zum Ton gehört. Entschieden wird das am Server (die Rolle, nicht der
   * Client); hier steht nur, was gar nicht erst zur Auswahl kommt.
   *
   * **Für Mitlesende gibt es deshalb keine Dateiauswahl mehr.** Sie war der
   * Umweg: der iOS-Dialog bot dort „Fotos" und „Foto aufnehmen" an, obwohl
   * beides abgewiesen wurde, und wer wirklich ein GIF wollte, musste es sich
   * vorher irgendwo besorgen. GIFs kommen jetzt aus der Auswahl daneben.
   */
  /**
   * **Zwei Flaggen statt einer** (#59).
   *
   * Bis hierher hieß es „der Autor darf Dateien, alle anderen nicht". Seit
   * #59 gehört ein Foto auch in eine Antwort; ein Video bleibt beim Bericht,
   * weil es Kontingent in einer anderen Größenordnung kostet und in Stücken
   * hereinkommt. Die Trennung steht deshalb hier und nicht an fünf Stellen
   * verteilt — und sie heißt, was sie meint.
   */
  const istReisender = reise?.role === 'sender'
  const reiseKarte = reise?.map_style
  const darfFotos = true
  // Ohne GIPHY_API_KEY steht der Knopf nicht da -- dann bleibt es bei Worten.
  const gifsAktiv = useGifsAktiv()

  /**
   * Die Kacheln und alles, was sie füllt -- `hooks/useAttachments.ts`.
   *
   * Was hier stehen bleibt, ist die Zeile selbst: der Text, der Vorrat je Ort
   * (dort reisen Text und Kacheln zusammen) und das Senden.
   */
  const anhaenge = useAttachments({ tripId, reiseVersatz })
  const {
    fotos,
    setFotos,
    problem,
    problemOhneNetz,
    setProblem,
    angeheftet,
    beschaeftigt,
    fertige,
    wartet,
    add,
    nimmGif,
    nimmAusReise,
    entferne: remove,
  } = anhaenge
  /**
   * Wie viele Wege es überhaupt gibt — daran hängt, ob es den `+` gibt.
   *
   * An einer Stelle abgeleitet und nicht zweimal hingeschrieben: das Blatt
   * zeigt dieselben Zeilen, die hier gezählt werden.
   */
  /**
   * **Eine Abstimmung gehört in den Reise-Chat und nicht in einen
   * Zweiergespräch.** Sie ist ein Beitrag an alle; im Verlauf mit einem
   * Einzelnen wäre sie eine Abstimmung mit einem Menschen, und das ist eine
   * Frage. Deshalb `withUser === undefined` und nicht nur die Rolle.
   */
  const darfAbstimmung = istReisender && withUser === undefined
  /*
   * **The weather (0.31.0) under the same conditions as the poll** -- a post
   * to everyone, by the traveller -- and only if the server can ask Apple:
   * without the keys the entry is not there at all.
   */
  const einstellungen = useSettings()
  const darfWetter = darfAbstimmung && einstellungen.data?.services?.weather === true
  // A chosen place and hour (#83) needs the place search as well -- Apple Maps, not only WeatherKit.
  const darfWetterAnderswo = darfWetter && einstellungen.data?.services?.place === true
  /*
   * The day card (#79) under the same conditions as the poll -- a post to
   * everyone, by the traveller. Whether there is a route with a day before
   * today says the server when asked; without one the tap ends in a
   * sentence, not in silence.
   */
  const darfTag = darfAbstimmung
  /**
   * "Aus dieser Reise" in the chat (1.52.0): what hangs at a point or spot
   * and is not in the chat yet. The traveller only -- a reader has nothing
   * at a point, and the list would be empty.
   */
  const darfAusReise = istReisender
  /**
   * Musik (#102): wie die Abstimmung ein Beitrag an alle, also nur der Autor
   * / die Autorin und nur im Reise-Chat. **Und nur mit Netz** -- ob ein Link
   * etwas taugt, entscheidet der Server.
   */
  /*
   * **Without a net it stands too, greyed** (1.80.0). Until here it was
   * hidden offline while the other entries that need the net stayed --
   * from use: "Musik wird ausgeblendet, wenn offline; andere bleiben."
   * Decided: all of them stand, greyed, and a tap says why (`Anhangwahl`).
   */
  const darfMusik = darfAbstimmung
  /**
   * The day separator (#113): the traveller only, only in the trip chat --
   * a chapter line of the report, not of a private conversation. Without a
   * net it waits in the basket at the end; inserting it needs the net.
   */
  const darfTagestrenner = darfAbstimmung
  /**
   * "Link zur Karte" (1.113.0): a post to everyone with cards of points on
   * the map -- the author only, in the trip chat and in its draft.
   */
  const darfKartenlink = darfAbstimmung
  const [tagestrennerOffen, setTagestrennerOffen] = useState(false)
  const [trennerFehler, setTrennerFehler] = useState<string | null>(null)
  const trennerAnlegen = useTagestrennerAendern(tripId, withUser, entwurf)
  const trennerSchluessel = useSendKey(`${sendeOrt(tripId, withUser, entwurf)}:trenner`)
  // The proposal for a new separator (#113, #130) -- from the server, for
  // the place it goes: at the end the next count and today, inserted the
  // number between its neighbours and the chosen post's day. Asked when the
  // sheet opens; the sheet waits for the answer.
  const trennerListe = useTagestrenner(tripId, darfTagestrenner && tagestrennerOffen, insertion)
  const trennerVorgabe: TagestrennerAngaben = {
    day: insertion ? (trennerListe.data?.day ?? heuteSchluessel(null)) : heuteSchluessel(null),
    number: trennerListe.data?.next_number ?? 1,
    word: trennerListe.data?.day_word ?? reise?.day_word ?? 'day',
    note: '',
  }
  /** The one message line (1.80.0), owned by the chat page since 1.117.4. */
  const { meldung, melde, raeume, neueWahl, online } = meldezeile
  const [ausReiseOffen, setAusReiseOffen] = useState(false)
  const wege = wegeZaehlen(darfFotos, gifsAktiv, darfAbstimmung, darfWetter, darfTag, darfWetterAnderswo, darfAusReise, darfMusik, darfTagestrenner, darfKartenlink)

  /**
   * Was hinter dem `+` steht -- `usePlusMenu.ts`.
   *
   * Die Blätter selbst stehen weiter hier im Baum: sie sind Blöcke im Fluss
   * über der Zeile, keine Auflagen. Was umgezogen ist, sind die Wege dorthin.
   */
  const plus = usePlusMenu({ tripId, istEntwurf: entwurf, reiseKarte, tagAenderung, onTagAenderungFertig, insertion })
  const {
    anhangOffen,
    setAnhangOffen,
    gifOffen,
    setGifOffen,
    abstimmungOffen,
    setAbstimmungOffen,
    wetterwahlOffen,
    setWetterwahlOffen,
    tagwahl,
    setTagwahl,
    tagLaedt,
    tagWartenAbbrechen,
    film,
    cancelFilm,
    aktionStand,
    nachNachricht,
    ohneNetzSagen,
    wetterSchicken,
    wetterAnderswoSchicken,
    tagFragen,
    musikOffen,
    setMusikOffen,
    musikSchicken,
    kartenlinkOffen,
    setKartenlinkOffen,
    kartenlinkSchicken,
    tagSchicken,
  } = plus

  /**
   * Das Mikrofon -- `hooks/useRecording.ts`.
   *
   * Es endet beim fertigen Blob; was daraus wird, macht `nimmTon()` bei den
   * Anhängen, wo es das für Foto und Video auch macht.
   */
  const aufnahme = useRecording({
    deckeSekunden: einstellungen.data?.settings.voice_max_seconds,
    onFertig: (roh, undSenden) => void tonAnnehmen(roh, undSenden),
  })
  const { nimmtAuf, pausiert, seit, hinweis: tonHinweis } = aufnahme

  /**
   * Eine fertige Aufnahme wird ein Anhang -- und geht gleich hinaus, wenn der
   * Sendeknopf sie beendet hat (#63).
   *
   * **Eine Funktionsdeklaration und keine Konstante**: sie wird oben
   * gebraucht, und nur so steht sie dort schon.
   */
  async function tonAnnehmen(roh: Aufnahme, undSenden: boolean) {
    const { ok, hinweis } = await anhaenge.nimmTon(roh)
    if (hinweis !== null) aufnahme.sagHinweis(hinweis)
    /* **Erst jetzt vormerken, nicht beim Druck.** Beim Druck steht die
       Kachel noch nicht da; `submit()` fände nichts zu senden und nähme die
       Vormerkung gleich wieder zurück. Hier ist die Aufnahme oben und die
       Vormerkung greift im nächsten Zeichnen. */
    if (undSenden && ok) setSendeWennFertig(true)
  }

  /**
   * Gibt es überhaupt etwas zu senden?
   *
   * **An einer Stelle abgeleitet.** Daran hängt, ob rechts im Feld das
   * Wellenform oder der Sendeknopf steht -- und dass es immer genau eines ist.
   */
  const etwasZuSenden = text.trim() !== '' || fotos.length > 0

  /**
   * **A voice message goes alone** (1.116.0). From use: "eine
   * Sprachnachricht hat niemals eine Textnachricht -- korrekt (wie Wetter,
   * Statistik etc.)" and "nicht automatisch senden, sondern nur Hinweis, und
   * keine Texteingabemöglichkeit bzw. Text verwerfen". A recording that ends
   * at its ceiling stays as a tile (`useRecording`); while it does, the field
   * takes no text and the other ways to attach are greyed out, and the line
   * says why. The server refuses the rest (`MessagesController::voiceAlone()`).
   */
  const tonDa = fotos.some((foto) => foto.art === 'ton')

  /**
   * Zu lang -- **an einer Stelle abgeleitet, wie oben.**
   *
   * **Gekürzt wird hier nichts.** Das Feld ist ein `contentEditable` und
   * ungesteuert mit Absicht (siehe den Doc-Block weiter unten): ein
   * zurückgeschriebener Wortlaut setzte den Cursor ans Ende, mitten im
   * Tippen. Ein `maxLength` gibt es an einem solchen Feld ohnehin nicht.
   *
   * Also bleibt der Text stehen, der Zähler sagt es, und der Sendeknopf ist
   * aus. Das ist keine stille Absage: es steht als Satz da, nicht als
   * `title`.
   *
   * Gezählt wird in Codepunkten (`[...text]`), weil der Server das auch tut
   * (`mb_strlen`). `text.length` zählte ein Emoji als zwei und sperrte damit
   * früher als nötig.
   */
  const zuLang = [...text.trim()].length > grenzen.message

  /**
   * Liegt eine Bildschirmtastatur über der Anwendung?
   *
   * Daran hängt der zweite Platz in der Zeile: der Knopf der Sprachnachricht
   * verliert dort seinen Tipp (siehe unten am Zweig), also steht bei offener
   * Tastatur der Sendeknopf an seiner Stelle.
   */
  const tastaturOffen = useTastaturOffen()

  /**
   * Ein `contenteditable` wächst von selbst — gedeckelt wird in der CSS
   * (`max-height`). Geblieben ist die Funktion für den Ausgangszustand: nach
   * dem Leeren soll die Zeile wieder einzeilig dastehen.
   */
  function grow() {
    field.current?.scrollTo({ top: field.current.scrollHeight })
  }

  /**
   * **The input stays where it was written** (1.22.0, `lib/eingabevorrat.ts`).
   *
   * Chat and draft of the report are the same page element at the same
   * place in the tree, so this line is never unmounted by the switch and
   * kept its content: photos attached in the draft stood in the chat and
   * went to everyone with the next "Senden". The same between two trips.
   * Now the line puts text and tiles aside for the place it leaves and
   * takes back what waits for the place it enters; nothing wanders.
   *
   * Here stood: leaving the chat deletes the uploaded attachments on the
   * server. That is gone with it -- a stashed picture must survive the
   * switch. What is never taken back is swept after a day
   * (`Speicher::verwaist()`), and the photo picker calls it "noch nirgends".
   */
  const ort = eingabeSchluessel(tripId, withUser, entwurf)
  useEffect(() => {
    const gemerkt = eingabeHolen(ort)
    const wortlaut = gemerkt?.text ?? ''
    setText(wortlaut)
    setFotos(gemerkt?.fotos ?? [])
    setProblem(null)
    if (field.current !== null) schreibe(field.current, wortlaut)
    requestAnimationFrame(grow)
    return () => {
      eingabeMerken(ort, {
        text: field.current === null ? '' : lese(field.current),
        fotos: angeheftet.current,
      })
    }
    // `grow` and the setters are stable; the place is the only input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ort])

  /** Nach dem Absenden: Feld leer, Kacheln weg, Vorschauen freigegeben. */
  function aufraeumen() {
    const feld = field.current
    const gesendet = feld === null ? '' : lese(feld).trim()
    setText('')
    // Das Feld ist ungesteuert -- geleert wird es hier, ausdrücklich.
    if (feld !== null) schreibe(feld, '')
    anhaenge.leeren()
    // Der Bezug gilt für diese eine Nachricht. Bliebe er stehen, hinge die
    // nächste ungefragt am selben Beitrag.
    onBezugWeg?.()
    // A finished sentence from the plus menu goes with the message (1.142.0).
    nachNachricht()
    requestAnimationFrame(grow)
    leerBestaetigen(gesendet, 0)
  }

  /**
   * The key goes only once the field is **seen** empty (1.85.0).
   *
   * Rows 1599/1600 (and 1151/1152 before 1.75.0): the same words twice, two
   * keys, 2.4 s apart, from an iPhone. 1.75.0 binds the key to the content
   * for as long as the line stands, and `forget()` stood at the top of
   * `aufraeumen()` -- the key was gone the moment the post succeeded, on the
   * assumption that the field was empty from then on. The data say the text
   * was in the field again within two seconds, and a second tap made a new
   * key. The one way for a sent text to stand in the field again without a
   * person typing it is the field itself: on iOS a `contenteditable` cleared
   * while the keyboard still holds a pending correction can get the text
   * put back when that correction lands. Staged on no desktop browser, so
   * not asserted; measured instead, on the device, by the line this writes
   * into the diagnosis.
   *
   * So: clear, look again on the next frame and once more after 400 ms. The
   * exact sent text standing there again is not a person's doing (nobody
   * retypes a sentence in 400 ms) -- it is cleared again and noted. Until
   * the field is seen empty, the key is held: a second tap in that window
   * carries the first key, and the server hands back the row it has.
   * Anything else in the field is a new post and ends the key.
   */
  function leerBestaetigen(gesendet: string, versuch: number) {
    const pruefe = () => {
      const feld = field.current
      const jetzt = feld === null ? '' : lese(feld).trim()
      if (jetzt !== '' && jetzt === gesendet && feld !== null && versuch < 2) {
        notiere(`senden: Feld war nach dem Leeren wieder voll (${versuch + 1}. Mal), erneut geleert`)
        schreibe(feld, '')
        setText('')
        requestAnimationFrame(grow)
        leerBestaetigen(gesendet, versuch + 1)
        return
      }
      if (versuch === 0 && jetzt === '') {
        leerBestaetigen(gesendet, 1)
        return
      }
      // Seen empty, or something else is being written: the post is gone --
      // the next one, even with the same words, is a new one.
      sendeSchluessel.forget()
    }
    if (versuch === 0) requestAnimationFrame(pruefe)
    else window.setTimeout(pruefe, 400)
  }

  /**
   * In den Ausgangskorb legen, statt zu verlieren.
   *
   * Mitgegeben werden die **Bilder**, nicht ihre ids -- auch die schon
   * hochgeladenen. Ein Foto, das vor dem Netzabbruch durchkam, hat zwar eine
   * id, aber die räumt der Server nach einem Tag weg; ein Eintrag, der morgen
   * früh darauf zeigt, wäre kaputt. Lieber einmal doppelt hochladen.
   */
  function inDenKorb(body: string, clientKey: string) {
    // Foto und Vorschaubild bleiben paarweise: derselbe Filter für beide Listen.
    const mitBild = fotos.filter(
      (foto): foto is Attachment & { blob: Blob } => foto.blob !== undefined && foto.art !== 'ton',
    )
    // The voice message goes with it (1.80.0) -- one per post, which is what
    // the microphone makes; `submit` says so if there were two.
    const ton = fotos.find((foto): foto is Attachment & { blob: Blob } => foto.art === 'ton' && foto.blob !== undefined)
    void merken({
      tripId,
      withUser,
      body,
      clientKey,
      // Der Bezug muss den Korb überstehen: ohne ihn käme die Antwort später
      // ohne ihren Anfang heraus, und niemand wüsste mehr, worauf sie ging.
      replyToId: bezug?.message.id,
      replyToMediaId: bezug?.mediaId,
      entwurf: entwurf,
      bilder: mitBild.map((foto) => foto.blob),
      vorschauen: mitBild.map((foto) => foto.thumb ?? null),
      ...(ton === undefined ? {} : { ton: { daten: ton.blob, dauerMs: ton.dauerMs ?? 0 } }),
    }).then(aufraeumen)
  }

  /**
   * **Die Leiste steht nur da, solange etwas markiert ist.**
   *
   * Gewünscht war sie „nicht als feste Symbole, sondern als Unterpunkt im
   * Pop-Up-Menü". In Apples eigenes Menü kommt eine Webseite nicht hinein —
   * das kann nur eine native App. Das Nächstliegende ist dies: sie erscheint
   * beim Markieren und verschwindet wieder. Angeschlagen wird sie über
   * `selectionchange`, denn ein `onSelect` gibt es an einem
   * `contenteditable` nicht.
   */
  /**
   * Welche Fassung des Hinweises passt.
   *
   * **Gemessen und nicht geraten:** die Breite eines Textes hängt an der
   * Schrift, und die hängt seit der Schriftgrößen-Einstellung am Gerät.
   * `measureText` fragt genau die Schrift, die im Feld steht — eine
   * Faustzahl („unter 400 Punkten die kurze") ginge bei der nächsten Stufe
   * daneben.
   */
  const [stufe, setStufe] = useState(0)
  useEffect(() => {
    const feld = field.current
    if (feld === null) return

    const messen = () => {
      const stil = getComputedStyle(feld)
      // **Das Sinnbild steht vor dem Text und nimmt Platz.** Es hier
      // abzuziehen ist die einzige Stelle, an der die Staffel davon weiß --
      // sonst wählte sie eine Fassung, die dann doch nicht hineinpasst.
      const platz = feld.clientWidth
        - Number.parseFloat(stil.paddingLeft)
        - Number.parseFloat(stil.paddingRight)
        - SINNBILD_PLATZ
      if (platz <= 0) return
      const stift = document.createElement('canvas').getContext('2d')
      if (stift === null) return
      stift.font = `${stil.fontStyle} ${stil.fontWeight} ${stil.fontSize} ${stil.fontFamily}`
      const passt = platzhalter.findIndex((wort) => stift.measureText(wort).width <= platz)
      setStufe(passt === -1 ? platzhalter.length - 1 : passt)
    }

    messen()
    const beobachter = new ResizeObserver(messen)
    beobachter.observe(feld)
    return () => beobachter.disconnect()
  }, [platzhalter])

  function submit() {
    const body = text.trim()
    // **Jeder stille Ausgang wird aufgeschrieben** (#28). Ohne das ist „geht
    // manchmal, manchmal nicht" nicht zu beantworten.
    if (!body && fotos.length === 0) return notiere('submit: nichts da')
    // **Auch hier und nicht nur am Knopf.** Die Eingabetaste sendet ebenfalls,
    // und eine Regel, die nur an einer der beiden Türen steht, ist keine.
    if (zuLang) return notiere('submit: zu lang')
    if (send.isPending) return notiere('submit: läuft noch')

    // Noch nicht fertig: nicht abweisen, sondern vormerken. Wer unterwegs
    // Fotos schickt, soll das Telefon weglegen können.
    if (beschaeftigt) {
      setSendeWennFertig(true)
      return notiere('submit: vorgemerkt, Foto noch nicht fertig')
    }

    setSendeWennFertig(false)

    // Ein Schlüssel für diese eine Nachricht: geht der Versuch am Netz
    // kaputt, wandert **derselbe** in den Korb. Sonst stünde der Beitrag
    // doppelt da, wenn der Server ihn längst hatte und nur die Antwort
    // verlorenging.
    //
    // **Und derselbe auch beim nächsten Tipp** (1.75.0): gleicher Inhalt,
    // gleicher Schlüssel, bis das Feld wirklich geleert ist. Hier stand
    // `neuerSchluessel()` je Tipp -- am Flughafen kam eine Nachricht an,
    // ihre Antwort nicht, der Text blieb stehen, und der zweite Tipp mit
    // neuem Schlüssel schrieb sie ein zweites Mal (1151/1152, 2,8 s).
    const clientKey = sendeSchluessel.keyFor(
      JSON.stringify([
        body,
        fotos.map((foto) => foto.id ?? `lokal-${foto.key}`),
        bezug?.message.id ?? null,
        bezug?.mediaId ?? null,
        // Where it goes is part of the post (#130), like whom it goes to.
        insertion === null ? null : [insertion.where, insertion.id],
      ]),
    )

    /*
     * **Und wer schreibt, während noch etwas wartet, stellt sich hinten an**
     * (1.44.0).
     *
     * Aus dem Betrieb, im Reisebericht: „die Nachrichten sind in einer
     * falschen Reihenfolge -- die online geschriebene Nachricht hat sich
     * dazwischengeschummelt." Genau so war es gebaut: der Reisebericht hängt
     * die Beiträge in der Reihenfolge an, in der sie beim Server **ankommen**.
     * Wer Netz hat, ging sofort hinaus und überholte damit alles, was noch im
     * Korb lag.
     *
     * Deshalb entscheidet nicht mehr „habe ich Netz", sondern „ist die
     * Schlange leer". Steht noch etwas darin, geht der neue Beitrag denselben
     * Weg -- und der Korb schickt der Reihe nach.
     *
     * **Eines geht nicht mit, und das steht hier statt in einer Fußnote:** ein
     * Video und ein GIF überleben den Korb nicht (das Video wegen seiner
     * Größe, das GIF, weil es nur eine id trägt). Ein Beitrag damit geht
     * weiterhin sofort hinaus und kann eine wartende Reihe überholen. Das ist
     * der Rest der Lücke; er ist selten und bekannt. Die Sprachnachricht ist
     * seit 1.80.0 nicht mehr darunter: ihre Bytes bleiben an der Kachel.
     */
    const kannWarten = fotos.every((foto) => foto.blob !== undefined)
      && fotos.filter((foto) => foto.art === 'ton').length <= 1
    // **Eine Absage ist keine Schlange.** Ein Eintrag mit `problem` wird von
    // `senden()` übersprungen und liegt, bis jemand ihn verwirft -- sich
    // dahinterzustellen hieße, nie wieder etwas hinauszuschicken.
    const schlangeDa = wartend.some((eintrag) => eintrag.problem === undefined)

    // **Inserting needs the net** (#130, decided from use: without one only
    // a new post at the end). The basket knows no place; the words stay in
    // the field and the line says why.
    if (insertion !== null && (!netzStand() || fotos.some((foto) => foto.stufe === 'offline'))) {
      setProblem(t.chat.einfuegen.brauchtNetz, true)
      return notiere('submit: einfügen ohne Netz')
    }

    // Ohne Netz -- oder mit einem Bild, das deshalb liegengeblieben ist --
    // wird gar nicht erst versucht.
    if (insertion === null && (!netzStand() || fotos.some((foto) => foto.stufe === 'offline') || (schlangeDa && kannWarten))) {
      /*
       * **Was nicht mitkann, wird gesagt und nicht weggelassen** (1.24.0,
       * Codereview E1).
       *
       * In den Korb geht, was seine Bytes dabeihat (`blob`). Ein Video hat
       * sie absichtlich nicht -- zweihundert Megabyte gehören nicht in
       * IndexedDB --, und eine Sprachnachricht und ein GIF liegen schon auf
       * dem Server und tragen nur eine id.
       *
       * Beim Video stand hier ein Satz, bei den beiden anderen **nichts**:
       * der Beitrag ging später ohne die Aufnahme hinaus, und niemand erfuhr
       * es. Gefragt wird deshalb nicht mehr nach der Art, sondern danach, was
       * den Korb überhaupt überlebt -- eine vierte Art fiele sonst wieder
       * still heraus.
       */
      // A second voice message is the one thing with bytes the basket does
      // not take (one per post, `inDenKorb`).
      const zweiterTon = fotos.filter((foto) => foto.art === 'ton' && foto.blob !== undefined)[1]
      const bleibtLiegen = fotos.find((foto) => foto.blob === undefined) ?? zweiterTon
      if (bleibtLiegen !== undefined) {
        setProblem(
          bleibtLiegen.bestand === true
            ? t.chat.reisefotoBrauchtNetz
            : bleibtLiegen.art === 'video'
              ? t.bausteine.medien.videoBrauchtNetz
              : bleibtLiegen.art === 'ton'
                ? t.chat.tonBrauchtNetz
                : t.chat.gifBrauchtNetz,
          true,
        )
        return notiere(`submit: ${bleibtLiegen.art} ohne Netz`)
      }
      inDenKorb(body, clientKey)
      onGesendet?.()
      return notiere('submit: in den Ausgangskorb')
    }

    if (!body && fertige.length === 0) return notiere('submit: nichts Fertiges')

    // **Jetzt springen und nicht erst bei der Antwort.** Wer sendet, will
    // sehen, dass es unten steht -- und bis der Server antwortet, vergeht auf
    // einem Telefon eine spürbare Weile. Kommt die Nachricht später an, steht
    // der Verlauf ohnehin schon am Ende und bleibt dort. **Nicht beim
    // Einfügen** (#130): der Beitrag steht dann nicht am Ende, und die Seite
    // springt zu ihm, sobald er da ist (`onDone`).
    if (insertion === null) onGesendet?.()
    notiere('submit: geht raus')

    send.mutate(
      {
        body,
        mediaIds: fertige.map((foto) => foto.id!),
        clientKey,
        replyToId: bezug?.message.id,
        replyToMediaId: bezug?.mediaId,
        entwurf: entwurf,
        insertion: insertionPlace(insertion),
      },
      {
        onSuccess: (message) => {
          aufraeumen()
          if (insertion !== null) {
            insertion.onDrop()
            insertion.onDone(message.id)
          }
        },
        onError: (fehler) => {
          // Am Netz gescheitert: nicht verloren, sondern verschoben. Eine
          // Absage des Servers dagegen bleibt eine Absage -- sie stünde im
          // Korb bis in alle Ewigkeit. Beim Einfügen gibt es keinen Korb
          // (#130): die Worte bleiben stehen, die Zeile sagt warum.
          if (fehler instanceof ApiError && fehler.offline) {
            if (insertion === null) inDenKorb(body, clientKey)
            else setProblem(t.chat.einfuegen.brauchtNetz, true)
          }
        },
      },
    )
  }

  /**
   * **Und derselbe Griff ein zweites Mal, tiefer.**
   *
   * Aus dem Betrieb (#28): „Pfeiltaste in der Nachricht schließt nur die
   * Bildschirmtastatur; zum Senden muss man nochmal drücken." Auf iOS kommt
   * der Druck auf die Eingabetaste in einem `contenteditable` nicht
   * verlässlich als `keydown` an -- er wird von der Autokorrektur oder vom
   * Schließen der Tastatur verbraucht. `beforeinput` kommt danach und sagt,
   * **was der Benutzer wollte**, statt welche Taste unterwegs war:
   * `insertParagraph` ist die Eingabetaste, `insertLineBreak`
   * Umschalt+Eingabe. Die Unterscheidung, die oben `shiftKey` macht, steht
   * hier also schon im Ereignis.
   *
   * **Ein echter Zuhörer und kein `onBeforeInput`.** React 19 reicht das
   * Ereignis zwar durch, aber `inputType` steht nur am nativen darunter; der
   * erste Anlauf las es am synthetischen und tat deshalb gar nichts -- der
   * Prüflauf hat es mit „0 Anfragen" gefangen.
   *
   * **Zweimal gesendet wird nichts:** greift `keydown`, hält sein
   * `preventDefault` das `beforeinput` auf; kommt kein `keydown`, ist dies
   * der einzige Weg. Der Ref auf `submit` hält den Zuhörer frisch, ohne ihn
   * bei jedem Zeichen neu zu hängen.
   */
  /**
   * Was die Eingabetaste tut -- an einer Stelle für beide Wege.
   *
   * **Die Regel kommt aus dem Betrieb (#28)** und ist die von iMessage und
   * WhatsApp: mit einer angeschlossenen Tastatur sendet sie, auf der
   * Bildschirmtastatur macht sie einen Absatz. Woran die beiden Fälle
   * auseinandergehalten werden, steht in `bildschirmtastaturVerdeckt()`.
   *
   * **Und im leeren Feld tut sie gar nichts.** Aus derselben Meldung: „ENTER
   * funktioniert, wenn noch keine Eingabe erfolgt ist -- dann werden
   * Leerzeilen erzeugt." Ein Absatz vor dem ersten Zeichen ist kein Absatz,
   * sondern eine leere Zeile, die niemand wollte.
   */
  function eingabetaste(event: { preventDefault: () => void }) {
    if (bildschirmtastaturVerdeckt()) {
      // Der Absatz ist die Vorgabe des Browsers -- außer im leeren Feld.
      if (text.trim() === '') event.preventDefault()
      return
    }
    event.preventDefault()
    submit()
  }

  const taste = useRef(eingabetaste)
  taste.current = eingabetaste

  useEffect(() => {
    const el = field.current
    if (el === null) return
    const aufAbsatz = (event: Event) => {
      if ((event as InputEvent).inputType !== 'insertParagraph') return
      taste.current(event)
    }
    el.addEventListener('beforeinput', aufAbsatz)
    return () => el.removeEventListener('beforeinput', aufAbsatz)
  }, [])

  // Der vorgemerkte Versand, sobald das letzte Bild durch ist. Ein
  // gescheitertes Bild nimmt die Vormerkung zurück -- sonst ginge der Beitrag
  // ohne das Foto raus, das man eigentlich zeigen wollte.
  useEffect(() => {
    if (!sendeWennFertig || beschaeftigt) return
    if (fotos.some((foto) => foto.stufe === 'fehler')) {
      setSendeWennFertig(false)
      setProblem(t.chat.fotoKamNichtDurch)
      return
    }
    setSendeWennFertig(false)
    submit()
    // submit hängt an Text und Fotos; die stehen zu diesem Zeitpunkt fest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendeWennFertig, beschaeftigt])

  /*
   * **Each place reports to the one line** (1.80.0, `useMeldezeile`). They
   * keep their own state -- that is where the reason lives and where it is
   * cleared -- and each change of it becomes the newest message. A clear
   * clears only what that place reported, never a newer one from elsewhere.
   */
  // Opening the plus menu is a new choice: an old error goes (1.117.4).
  useEffect(() => {
    if (anhangOffen) neueWahl()
  }, [anhangOffen, neueWahl])

  useEffect(() => {
    if (aktionStand === null) raeume('plus')
    else melde('plus', aktionStand.text, aktionStand.art, aktionStand.ohneNetz)
  }, [aktionStand, melde, raeume])
  useEffect(() => {
    if (tonHinweis === null) raeume('ton')
    else melde('ton', tonHinweis)
  }, [tonHinweis, melde, raeume])
  useEffect(() => {
    if (!tonDa) {
      raeume('sprache')
      return
    }
    // What stood in the field goes, and the line says so.
    const feld = field.current
    const verworfen = feld !== null && lese(feld).trim() !== ''
    if (verworfen && feld !== null) {
      schreibe(feld, '')
      setText('')
      requestAnimationFrame(grow)
      notiere('sprache: Text im Feld verworfen')
    }
    setAnhangOffen(false)
    setGifOffen(false)
    melde('sprache', verworfen ? t.chat.spracheTextVerworfen : t.chat.spracheAllein, 'hinweis')
    // `grow`, `field` and the setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tonDa, melde, raeume])
  useEffect(() => {
    if (problem === null) raeume('anhang')
    else melde('anhang', problem, 'fehler', problemOhneNetz)
  }, [problem, problemOhneNetz, melde, raeume])
  // The reason a tile failed -- it stood as a line of its own until here.
  const kachelFehler = [...new Set(fotos.filter((f) => f.stufe === 'fehler').map((f) => f.problem))]
    .filter(Boolean)
    .join(' · ')
  useEffect(() => {
    if (kachelFehler === '') raeume('kachel')
    else melde('kachel', kachelFehler)
  }, [kachelFehler, melde, raeume])
  // A refusal of the server when sending; a failure at the net goes into the
  // basket and is no message.
  const sendeFehler = send.isError && !(send.error instanceof ApiError && send.error.offline)
    ? (send.error instanceof Error ? send.error.message : t.chat.nachrichtNichtRaus)
    : null
  useEffect(() => {
    if (sendeFehler === null) raeume('senden')
    else melde('senden', sendeFehler)
  }, [sendeFehler, melde, raeume])

  if (disabled) {
    return (
      <p className="composer__hint">
        {disabledHint ?? t.chat.chatGeschlossen}
      </p>
    )
  }

  /**
   * Der Sendeknopf -- **einmal geschrieben, an zwei Stellen gestellt.**
   *
   * Aus dem Betrieb (#63): „nach Auswahl der Fotos: der Sendeknopf ist nicht
   * in der Textzeile sondern außerhalb [...] so dass man sieht dass das Senden
   * die Fotos und den Text auslöst." Er steht deshalb mit Anhang oben an der
   * Kachelreihe und ohne Anhang wie bisher im Feld.
   *
   * **Zwei Kopien davon wären zwei Wahrheiten.** An diesem Knopf hängt der
   * Ablauf aus #28 -- gesendet wird beim Aufsetzen des Fingers, nicht beim
   * Loslassen -- und der ist mühsam genug erarbeitet, um ihn nicht zu
   * verdoppeln. Verschieden ist allein die Klasse.
   */
  const sendeknopf = (klasse: string) => (
    <button
      type="button"
      className={klasse}
      disabled={!etwasZuSenden || zuLang || send.isPending || sendeWennFertig}
      /* **Gesendet wird beim Aufsetzen, nicht beim Loslassen.**

         Aus dem Betrieb (#28): „Senden-Knopf in der Eingabezeile: schließt
         zuerst die Tastatur, noch einmal drücken zum Senden." Der Ablauf
         dahinter: das Aufsetzen des Fingers gibt dem Knopf den Fokus, das Feld
         verliert ihn, die Tastatur fährt herunter -- und weil die Anwendung
         dabei wächst, wandert der Knopf unter dem Finger weg. Das `click`
         kommt nie an. Dieselbe Falle hat im Prüflauf zu #25 den Anmeldeknopf
         verfehlt.

         **Der erste Anlauf hat versucht, den Fokus zu halten**
         (`preventDefault` am Aufsetzen), und am iPad schien er zu greifen. Die
         Erklärung dafür war falsch und ist es wert, richtiggestellt zu werden:
         nicht weil dort keine Bildschirmtastatur im Spiel wäre -- es wurde mit
         beiden getestet --, sondern weil sie am iPad nach dem Senden **offen
         bleibt** und von Hand geschlossen wird. Bleibt sie stehen, wächst die
         Anwendung nicht, und der Knopf wandert nicht weg. Am iPhone fuhr sie
         herunter, und genau dort fiel es auf.

         Also hängt das Senden gar nicht mehr daran. Es geschieht beim
         Aufsetzen; was danach mit Fokus, Tastatur und Lage passiert, ist der
         Nachricht egal. `preventDefault` bleibt trotzdem stehen -- wo es
         greift, bleibt die Tastatur offen und man kann weiterschreiben.

         **`detail === 0` trennt die Tastatur vom Finger.** Ein `click` ohne
         Zeigerdruck kommt von der Eingabe- oder Leertaste auf dem fokussierten
         Knopf; nur der braucht hier noch einen Weg. Sonst würde jeder
         Fingerdruck zweimal senden. */
      onPointerDown={(event) => {
        notiere(`Knopf: Druck (${event.pointerType})`)
        event.preventDefault()
        submit()
      }}
      onClick={(event) => {
        notiere(`Knopf: Klick (detail ${event.detail})`)
        if (event.detail === 0) submit()
      }}
      onTouchStart={() => notiere('Knopf: Finger auf')}
      aria-label={t.chat.senden}
      title={t.chat.senden}
    >
      {/* **Ein echtes `<span>` und kein `::before`.** Die Trefferfläche bleibt
          44 Punkte, der sichtbare Kreis ist kleiner -- sonst füllte er die
          Pille bis an den Rand. Auf einem `<button>` ist eine Auflage in
          diesem Projekt ein Element, seit die GIF-Kacheln das gekostet
          haben. */}
      <span className="composer__kreis">
        <PfeilHochIcon size={19} />
      </span>
    </button>
  )

  /*
   * **Mit Anhang ist die Eingabezeile ein Block, ohne Anhang eine Zeile.**
   * Die Marke trägt beides: die Trennlinie zwischen Kacheln und Textzeile
   * faellt weg (aus zwei Streifen wird einer mit einer Kante nach oben), und
   * das Feld bekommt den Platz zurück, den der Knopf darin belegt hat.
   */
  // The sentence about the tiles stands in the one message line now
  // (1.80.0), so the row of tiles is there only when there are tiles.
  const mitAnhang = fotos.length > 0

  return (
    <div className={mitAnhang ? 'composer-area composer-area--anhang' : 'composer-area'}>
      {/* Worauf das hier antwortet -- direkt über dem Feld, in dem man
          schreibt. Weiter oben, bei der Sprechblase selbst, wäre es beim
          Tippen längst aus dem Bild. */}
      {/* Before or after a chosen post (#85, #130): where the next post goes,
          above the field like a quote -- and the X takes it back. The same
          marker in the chat and in the draft. */}
      {insertion !== null && (
        <div className="bezug composer__nach" data-einfuegen={insertion.where} data-einfuegen-ziel={insertion.id}>
          <span className="bezug__text">
            <span className="bezug__auszug">
              {insertion.where === 'before' ? t.chat.einfuegen.leisteDavor(insertion.text) : t.chat.einfuegen.leisteDanach(insertion.text)}
            </span>
          </span>
          <button
            type="button"
            className="icon-button bezug__weg"
            title={t.chat.einfuegen.weg}
            aria-label={t.chat.einfuegen.weg}
            onClick={insertion.onDrop}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}
      {bezug && (
        <div className="bezug">
          {/* `Standbild` and not a plain `<img>` (1.78.0): a video without its
              poster yet asks `/poster`, which is a 404 for a while -- the plain
              image kept the broken sign, this one waits and asks again. */}
          {bezugBild(bezug) && <Standbild className="bezug__bild" src={bezugBild(bezug)!} />}
          <span className="bezug__text">
            <span className="bezug__autor">
              Antwort an {bezug.message.author.display_name}
              {bezug.mediaId !== undefined && ` · ${medienwort(bezugMedium(bezug)?.kind)}`}
            </span>
            <span className="bezug__auszug">
              {bezug.message.body !== '' ? <Ausgezeichnet text={bezug.message.body} /> : medienwort(bezugMedium(bezug)?.kind)}
            </span>
          </span>
          <button
            type="button"
            className="icon-button bezug__weg"
            title={t.chat.bezugEntfernen}
            aria-label={t.chat.bezugEntfernen}
            onClick={() => onBezugWeg?.()}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}

      {/* **Erst kurz vor Schluss.** Ein Zähler, der von Zeichen eins an
          mitläuft, ist Aufsicht; einer, der bei neunzig Prozent erscheint,
          ist eine Warnung. Er steht über den Anhängen und damit über der
          Eingabezeile -- dort, wo auch die anderen Sätze zum Zustand stehen. */}
      <Restzeichen wert={text} grenze={grenzen.message} />

      {/* **Der Sendeknopf gehoert dem ganzen Block, nicht der Textzeile.**

          Aus dem Betrieb (#63): mit Anhang standen hier zwei getrennte
          Streifen uebereinander -- Kacheln oben, Textzeile unten, jede mit
          eigener Kante --, und der Knopf sass im Feld der unteren. Er sah
          damit aus, als gaelte er dem Text.

          **Der Knopf scrollt nicht mit.** Die Kachelreihe scrollt waagerecht;
          laege er darin, waere er bei sechs Fotos aus dem Bild. Er ist deshalb
          ein Geschwister der Reihe und kein Kind.

          **Waehrend einer Aufnahme steht er nicht da** -- dort gibt es nichts
          zu senden, was nicht erst beendet werden muesste. Das aendert sich
          mit dem neuen Aufnahmeablauf; heute ist es der Zustand von vorher. */}
      {mitAnhang && (
        <div className="anhangzeile">
          <div className="attachments">
            {fotos.map((foto) => (
              <Kachel key={foto.key} foto={foto} onRemove={() => remove(foto)} />
            ))}

          </div>
          {!nimmtAuf && sendeknopf('anhangzeile__senden')}
        </div>
      )}

      {/* **Was da rechnet, gehoert benannt.** Ein Balken ohne Wort sagt „warte",
          aber nicht worauf -- und Verkleinern ist das einzige, was hier lange
          dauern kann. */}
      {fotos.some((f) => f.stufe === 'rechnet' && f.art === 'video') && (
        <p className="composer__status" role="status">
          {t.chat.videoWirdVerkleinert}
        </p>
      )}

      {fotos.some((f) => f.hinweis) && (
        <p className="composer__status" role="status">
          {[...new Set(fotos.map((f) => f.hinweis))].filter(Boolean).join(' · ')}
        </p>
      )}

      {(wartet > 0 || sendeWennFertig) && (
        <p className="composer__status" role="status">
          {sendeWennFertig
            ? t.chat.sendetSpaeter
            : t.chat.fertigVon(fertige.length, fotos.length)}
        </p>
      )}

      {/* **Nur noch im Rückfall.** Der Satz stand hier unter *jedem* Video --
          auch unter einem, das gerade von 33 MB auf 2,4 MB geschrumpft war,
          und widersprach damit der Zeile direkt darüber. Aus dem Betrieb, am
          Bildschirmfoto.

          Weg kommt er trotzdem nicht: kann ein Gerät nicht umrechnen, geht
          das Video wirklich unverändert hinaus, und dann ist „Am
          kompatibelsten" wieder der einzige Hebel, den der Absender hat. Der
          Grund steht davor (`hinweis`), der Rat dahinter. */}
      {fotos.some((f) => f.art === 'video' && f.hinweis) && (
        <p className="composer__status">
          {t.chat.kompatibelHinweis} <strong>{t.chat.kompatibelBetont}</strong>
          {t.chat.kompatibelRest}
        </p>
      )}

      {/* **One line for what there is to say** (1.80.0, `useMeldezeile`):
          the newest message wins, a sentence about the missing net is red and
          goes when the net returns. `data-aktion-stand` stays on what the
          plus menu reports -- two prüfstand runs wait for it. */}
      {meldung !== null && (
        <p
          className={`composer__status${meldung.art === 'fehler' ? ' composer__status--fehler' : ''}`}
          role={meldung.art === 'fehler' ? 'alert' : 'status'}
          data-meldung={meldung.quelle}
          data-ohne-netz={meldung.ohneNetz ? '' : undefined}
          data-aktion-stand={meldung.quelle === 'plus' ? '' : undefined}
        >
          {meldung.text}
        </p>
      )}

      {anhangOffen && (
        <Anhangwahl
          darfDateien={darfFotos}
          // **Ohne Netz ebenfalls nur Fotos** (1.45.0): ein Video überlebt den
          // Ausgangskorb nicht, also wird es gar nicht erst angeboten. Die
          // Sperre selbst steht in `useAttachments` -- eine Beschriftung ist
          // so wenig eine Sperre wie `accept`.
          nurFotos={!istReisender || !online}
          nurFotosGrund={istReisender && !online ? t.chat.ohneNetzKeinVideo : undefined}
          ohneNetz={!online}
          onOhneNetz={(text) => {
            setAnhangOffen(false)
            ohneNetzSagen(text)
          }}
          ausReise={darfAusReise}
          gifs={gifsAktiv}
          abstimmung={darfAbstimmung}
          wetter={darfWetter}
          wetterAnderswo={darfWetterAnderswo}
          tag={darfTag}
          musik={darfMusik}
          onMusik={() => {
            setAnhangOffen(false)
            setMusikOffen(true)
          }}
          tagestrenner={darfTagestrenner}
          kartenlink={darfKartenlink}
          onKartenlink={() => {
            setAnhangOffen(false)
            setKartenlinkOffen(true)
          }}
          onTagestrenner={() => {
            setAnhangOffen(false)
            setTagestrennerOffen(true)
          }}
          onTag={() => {
            setAnhangOffen(false)
            void tagFragen()
          }}
          onAbstimmung={() => {
            setAnhangOffen(false)
            setAbstimmungOffen(true)
          }}
          onWetter={() => {
            setAnhangOffen(false)
            void wetterSchicken()
          }}
          onWetterAnderswo={() => {
            setAnhangOffen(false)
            setWetterwahlOffen(true)
          }}
          onFotoOderVideo={() => {
            // **Erst öffnen, dann schliessen.** Der Griff ans Dateifeld muss
            // in derselben Geste liegen; iOS lässt ihn sonst fallen.
            picker.current?.click()
            setAnhangOffen(false)
          }}
          onGif={() => {
            setAnhangOffen(false)
            setGifOffen(true)
          }}
          onAusReise={() => {
            setAnhangOffen(false)
            setAusReiseOffen(true)
          }}
          onSchliessen={() => setAnhangOffen(false)}
        />
      )}

      {ausReiseOffen && (
        <Fotowahl
          tripId={tripId}
          ziel={{ art: 'chat' }}
          schonDa={fertige.map((foto) => foto.id!)}
          versatz={reiseVersatz}
          onSchliessen={() => setAusReiseOffen(false)}
          onFertig={(medien) => {
            setAusReiseOffen(false)
            nimmAusReise(medien)
          }}
        />
      )}

      {gifOffen && (
        <Gifwahl tripId={tripId} onGewaehlt={nimmGif} onZu={() => setGifOffen(false)} />
      )}

      {wetterwahlOffen && (
        <Wetterwahl
          tripId={tripId}
          versatz={reiseVersatz}
          onWahl={(wahl) => void wetterAnderswoSchicken(wahl)}
          onAbbruch={() => setWetterwahlOffen(false)}
        />
      )}

      {tagwahl === null && tagLaedt && <TagwahlWarten onAbbruch={tagWartenAbbrechen} />}

      {tagwahl !== null && (
        <Tagwahl
          tripId={tripId}
          vorschau={tagwahl}
          versatz={reiseVersatz}
          animation={animationFrom(reiseKarte)}
          filmPossible={canAnimate()}
          vorbelegt={tagAenderung?.day_card ? {
            from: tagAenderung.day_card.started_at,
            to: tagAenderung.day_card.ended_at,
            display: tagAenderung.day_card.display ?? 'both',
            film: tagAenderung.day_card.film ?? null,
            dayTo: tagAenderung.day_card.day_to ?? null,
            movingOnly: tagAenderung.day_card.moving_only === true,
            showRows: tagAenderung.day_card.show_rows !== false,
          } : undefined}
          keepable={tagAenderung?.day_card?.film?.input !== undefined && tagAenderung.media.some((m) => m.kind === 'video')}
          holen={(day, dayTo, movingOnly) => api.get<DayCardPreview>(`/api/trips/${tripId}/daycard`, { day, day_to: dayTo, moving_only: movingOnly ? '1' : undefined })}
          onWahl={(wahl) => void tagSchicken(wahl)}
          onAbbruch={() => {
            setTagwahl(null)
            onTagAenderungFertig?.()
          }}
        />
      )}

      {film !== null && <RouteFilmSheet progress={film} onCancel={cancelFilm} />}

      {abstimmungOffen && (
        <Abstimmungsformular tripId={tripId} entwurf={entwurf} insertion={insertion} onZu={() => setAbstimmungOffen(false)} />
      )}

      {/* The day separator's sheet (#113): its answer becomes the chip over
          the line; the post goes out with it, through the same door as every
          post -- key, outbox, draft. The proposal (next count, the trip's
          word, today in its clock) comes from the server when it answers in
          time, else from what the page knows. */}
      {/* A separator is a post of its own (#130): the sheet sends it at
          once, at the end or where the marker above says, and waits for the
          proposal of that place before it shows its fields. */}
      {tagestrennerOffen && (!online || (trennerListe.data !== undefined && !trennerListe.isFetching)) && (
        <Tagestrennerwahl
          vorgabe={trennerVorgabe}
          nachnummerieren={trennerListe.data?.renumber === true}
          laeuft={trennerAnlegen.isPending}
          fehler={trennerFehler}
          onWahl={(angaben, nachnummerieren) => {
            if (!netzStand()) {
              // Inserting needs the net (#130); at the end the separator
              // waits in the basket like a weather card and goes out as its
              // own post (`schicken()` in `lib/outbox.ts`).
              if (insertion !== null) {
                setTrennerFehler(t.chat.einfuegen.brauchtNetz)
                return
              }
              setTrennerFehler(null)
              setTagestrennerOffen(false)
              void merken({ tripId, entwurf, body: '', bilder: [], tagestrenner: angaben })
              return
            }
            setTrennerFehler(null)
            const clientKey = trennerSchluessel.keyFor(JSON.stringify([angaben, insertion === null ? null : [insertion.where, insertion.id]]))
            trennerAnlegen.mutate(
              { aktion: 'anlegen', angaben, insertion: insertionPlace(insertion), renumber: nachnummerieren, clientKey },
              {
                onSuccess: (antwort) => {
                  trennerSchluessel.forget()
                  setTagestrennerOffen(false)
                  if (insertion !== null) {
                    insertion.onDrop()
                    if ('id' in antwort) insertion.onDone(antwort.id)
                  } else {
                    onGesendet?.()
                  }
                },
                onError: (fehler) => setTrennerFehler(fehlertext(fehler)),
              },
            )
          }}
          onAbbruch={() => {
            setTrennerFehler(null)
            setTagestrennerOffen(false)
          }}
        />
      )}

      {kartenlinkOffen && (
        <Kartenlinkwahl
          tripId={tripId}
          ort={sendeOrt(tripId, undefined, entwurf)}
          onSenden={kartenlinkSchicken}
          onZu={() => setKartenlinkOffen(false)}
        />
      )}

      {musikOffen && (
        <Musikwahl onSenden={musikSchicken} onZu={() => setMusikOffen(false)} wohin="chat" ort={sendeOrt(tripId, undefined, entwurf)} />
      )}

      {/* **Die Reihe aus vier Knöpfen ist weg.**

          Seit die Eingabezeile ein `contenteditable` ist, bietet iOS von
          selbst *Format ▸ Fett · Kursiv · Unterstreichen* im eigenen
          Auswahlmenü an — dafür braucht es hier nichts mehr. Aus dem
          Betrieb: „die Knopfsymbole wegnehmen, die benötigen viel
          Anzeigeplatz."

          **Durchgestrichen und Kapitälchen kennt Apples Menü nicht**, und
          eine Webseite kann dort nichts hinzufügen — das kann nur eine native
          App. Die beiden stehen deshalb in der Eingabezeile selbst (unten),
          und zwar nur, solange etwas markiert ist: keine eigene Reihe, kein
          Platz im Ruhezustand. */}


      {/* **Beim Aufnehmen verschwindet die Eingabezeile ganz.**

          Aus dem Betrieb (#63): „die Eingabezeile verschwindet ganz,
          stattdessen kann der Platz für die Anzeige der Symbole (Pause)
          genutzt werden." Vorher stand die Aufnahme *neben* dem Feld: zwei
          kleine Knöpfe im Feld, und daneben ein Textfeld, das währenddessen
          nichts zu tun hatte -- man konnte hineintippen, während das Mikrofon
          lief. */}
      {nimmtAuf ? (
        <Aufnahmeleiste
          dauerMs={seit}
          pausiert={pausiert}
          kannPausieren={aufnahme.kannPausieren}
          pegel={aufnahme.pegel}
          onVerwerfen={aufnahme.abbrich}
          onPause={aufnahme.pausiere}
          onWeiter={aufnahme.weiter}
          onSenden={() => void aufnahme.beende(true)}
        />
      ) : (
      <div className="composer">
        {/* **Die Dateiauswahl steht seit #59 allen offen** -- Mitlesende
            wählen Fotos, der Autor dazu Videos. Vorher stand hier für alle
            anderen ein Dialog, der Fotos anbot und Fotos ablehnte. */}
        {darfFotos && (
          <>
            <input
              ref={picker}
              type="file"
              /* **`accept` ist keine Sperre** (steht so in CLAUDE.md), sondern
                 ein Vorschlag an die Dateiauswahl. Was ein Mitlesender
                 trotzdem auswählt, weist `add()` unten mit einem Satz ab --
                 und der Server ohnehin. */
              accept={istReisender ? 'image/*,video/*' : 'image/*'}
              multiple
              hidden
              onChange={(event) => {
                void add(event.target.files)
                // Zurücksetzen, sonst löst dieselbe Datei kein zweites Mal aus.
                event.target.value = ''
              }}
            />
            {/* **Hier stand ein zweites Dateifeld mit `capture="environment"`**
                -- der eigene Weg zur Kamera. Er ist heraus, auf Ansage aus
                dem Betrieb: „die Kamera aus dem Auswahlmenü herausnehmen (nur
                Foto/Video + GIF)."

                Verloren geht dabei nichts: die Foto-App eines Telefons hat
                einen Auslöser, und das Feld darüber führt dorthin. Gewonnen
                ist ein Weg weniger, der eigene Sonderfälle mitbrachte --
                `capture` ist der Weg, über den die gemeldeten 360p ins Haus
                kamen. */}
          </>
        )}

        {/* **Ein Knopf statt zweier.**

            Aus dem Betrieb: „am Smartphone ist das Eingabefeld zu schmal [...]
            oder durch ein + ersetzen, und dann ein Auswahlfenster mit
            Foto/Video / Kamera / GIF." Die Kamera ist auf spätere Ansage
            wieder heraus (siehe das Dateifeld darüber); geblieben ist der
            eine Knopf.

            Jeder Knopf hier ist 44 Punkte breit, weil er es sein muss; was die
            Knöpfe nehmen, fehlt dem Text. Zwei in einen zu legen gibt dem Feld
            52 Punkte zurück.

            **Bei nur einem Weg bleibt der Knopf, der er war.** Ein `+`, das
            ein Blatt mit einer einzigen Zeile öffnet, wäre ein Griff mehr für
            nichts -- und genau das ist die Lage der Mitlesenden, die GIFs
            schicken dürfen und sonst nichts. */}
        {/* **Und solange markiert ist, tritt es zurück** (1.69.0). Mit vier
            Auszeichnungen wäre die Zeile sonst 44 Punkte breiter als bisher;
            so bleibt sie es nicht. Der Handel ist keiner: wer gerade Text
            markiert, hängt in diesem Moment kein Foto an — und ein Tipp ins
            Feld bringt das `+` zurück, weil damit die Markierung geht. */}
        {hatAuswahl ? null : wege >= 2 ? (
          <button
            type="button"
            disabled={tonDa}
            className={
              anhangOffen
                ? 'composer__attach composer__attach--rund composer__attach--an'
                : 'composer__attach composer__attach--rund'
            }
            onClick={() => {
              setAnhangOffen((offen) => !offen)
              setGifOffen(false)
            }}
            aria-label={t.chat.etwasAnhaengen}
            aria-expanded={anhangOffen}
            title={t.chat.etwasAnhaengen}
          >
            <PlusIcon size={22} />
          </button>
        ) : (
          <>
            {darfFotos && (
              <button
                type="button"
                disabled={tonDa}
                className="composer__attach composer__attach--rund"
                onClick={() => picker.current?.click()}
                aria-label={
                  istReisender ? t.chat.fotosUndVideosHinzufuegen : t.chat.fotosHinzufuegen
                }
                title={istReisender ? t.chat.fotosUndVideosHinzufuegen : t.chat.fotosHinzufuegen}
              >
                <PhotoIcon size={20} />
              </button>
            )}
            {gifsAktiv && (
              <button
                type="button"
                disabled={tonDa}
                className={
                  gifOffen
                    ? 'composer__attach composer__attach--rund composer__attach--an'
                    : 'composer__attach composer__attach--rund'
                }
                onClick={() => setGifOffen((offen) => !offen)}
                aria-label={t.chat.gifWahl}
                aria-expanded={gifOffen}
                title={t.chat.gifWahl}
              >
                <GifIcon size={20} />
              </button>
            )}
          </>
        )}

        {/* **Alle vier, und nur solange markiert ist** (1.69.0).
            Hier standen zwei, mit der Begründung „Fett und Kursiv holt man
            sich in Apples Menü". Das Menü gibt es, und `lese()` liest sein
            `<b>` und `<i>` auch — der Satz war also nicht falsch, nur nicht
            genug. Aus dem Betrieb: „zusätzlich soll noch Fett und Italics
            angeboten werden."

            **Den Platz macht das `+`**, und zwar oben in dieser Zeile: wer
            gerade Text markiert, hängt kein Foto an. Gemessen auf 390 Punkten
            (`auszeichnung.mjs`): ohne Markierung hat das Feld 326, mit
            Markierung 182 — und mit stehendem `+` wären es 134. Das
            Zurücktreten bringt also genau 48 Punkte, und ohne sie wäre die
            Markierung in einem Feld zu lesen, das kaum ein Wort zeigt.

            Im Ruhezustand kosten die vier weiterhin nichts, sie stehen nur
            mit der Markierung da. */}
        {hatAuswahl && (
          <Auszeichnungsknoepfe lege={schreibfeld.lege} klasse="composer__attach" />
        )}

        {/* **Ein `contenteditable`, kein `textarea`.** Ein Textfeld kann eine
            Schrift haben, nicht zwei — fett sähe darin aus wie mager, und die
            Sternchen stünden weiter da. Warum der Trick mit einer Fassung
            dahinter nicht geht, steht in `reichtext.ts`.

            **Ungesteuert, mit Absicht.** Der Zustand wird aus dem Feld
            gelesen, nie hineingeschrieben; ein `value`, das React bei jedem
            Tastendruck zurückschreibt, setzte den Cursor ans Ende. Geschrieben
            wird nur, wenn die Anwendung es meint: nach dem Senden. */}
        {/* **Die Fassung um das Feld — und der Knopf liegt darin** (#8, #22).
            Aus dem Betrieb: „das Eingabefeld ist noch zu klein", daneben ein
            Bildschirmfoto von iMessage. Dort steht rechts vom Feld **kein**
            Knopf, sondern einer *im* Feld.

            Gemessen auf 390 Punkten: vorher blieben dem Text 182 Punkte
            (Feld 210 minus Innenabstand), jetzt sind es 254. Zwei
            44-Punkt-Knöpfe und ihre Abstände sind genau der Unterschied.

            **Der Knopf ist ein Geschwister des Feldes, kein Kind.** Ein
            `<button>` in einem `contenteditable` ist bearbeitbarer Inhalt:
            `lese()` schriebe ihn mit, und der Cursor könnte darin landen. */}
        <div className="composer__feld">
          <div
            ref={field}
            className="composer__input"
            contentEditable={!tonDa}
            data-gesperrt={tonDa ? 'sprache' : undefined}
            aria-disabled={tonDa}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            /* **Kein `enterKeyHint="send"` mehr.** Hier stand einmal die
               Begründung, die Taste heiße „Senden", weil sie das auch tut. Seit
               #28 tut sie es auf der Bildschirmtastatur *nicht* mehr -- dort
               macht sie den Absatz, wie in iMessage und WhatsApp. Eine blaue
               Sendetaste, die einen Absatz macht, wäre die Falle, die der alte
               Satz vermeiden wollte, nur andersherum. */
            aria-label={platzhalter[0]}
            /* Lesen und Einfügen kommen aus `useSchreibfeld` -- dort steht
               auch, warum: das Blatt zum Ändern braucht dieselben Schritte,
               und zweimal geschrieben laufen sie auseinander. */
            onInput={schreibfeld.onInput}
            onPaste={schreibfeld.onPaste}
            onKeyDown={(event) => {
              // **Die Eingabetaste sendet, überall.**
              //
              // Aus dem Betrieb: „Senden (wenn der Sendeknopf da ist): mit
              // ENTER." Hier stand vorher eine Abfrage auf `pointer: coarse`,
              // und die hat den Fall verfehlt, aus dem die Meldung kam: **ein
              // iPad mit angeschlossener Tastatur meldet einen groben
              // Zeiger.** Dort tippte also eine echte Tastatur, und die
              // Eingabetaste tat nichts. Ein Merkmal des *Bildschirms* kann
              // nicht beantworten, womit gerade geschrieben wird.
              //
              // Umschalt+Eingabe macht den Absatz, wie im Feld zum Ändern
              // einer Nachricht. Der Preis gehört genannt: am Telefon ist ein
              // Absatz damit umständlicher als vorher.
              //
              // **Ob es etwas zu senden gibt, entscheidet `submit()`** — und
              // sonst niemand. Hier stand zuerst `etwasZuSenden` davor, damit
              // Taste und Knopf dieselbe Bedingung tragen; die Gegenprobe hat
              // gezeigt, dass sie gar nichts entscheidet, weil `submit()` den
              // leeren Fall ohnehin abfängt. Eine dritte Kopie derselben Regel
              // ist die Stelle, an der sie auseinanderläuft.
              if (event.key === 'Enter' && !event.shiftKey) eingabetaste(event)
            }}
          />

          {/* **Der Platzhalter ist eine Auflage und kein `::before` mehr.**
              Aus dem Betrieb: die Hinweiszeile über der Zeile sollte weg und
              durch ein Sinnbild im leeren Feld ersetzt werden. In
              `content: attr(...)` kann kein SVG stehen.

              **Und kein Spiel mit dem Innenabstand.** Das Feld beim ersten
              Zeichen von `14 + Sinnbild` auf `14` zurückzusetzen ließe den
              Text sichtbar springen. Die Auflage liegt darüber; der Cursor
              steht die ganze Zeit an derselben Stelle.

              `aria-hidden`: derselbe Wortlaut steht als `aria-label` am Feld,
              und zweimal vorgelesen ist einmal zu viel. */}
          {text === '' && !nimmtAuf && (
            <span className="composer__leer" aria-hidden="true">
              {reichweite === 'alle' ? <GruppeIcon size={16} /> : <PersonIcon size={16} />}
              <span className="composer__leer-text">
                {platzhalter[stufe] ?? platzhalter[0]}
              </span>
            </span>
          )}

          {/* Während der Aufnahme liegt der Zähler über dem Feld statt daneben
              -- geschrieben wird dabei ohnehin nicht, und der Platzhalter
              darunter ist abgeschaltet. */}
          {/* **Genau einer, und er wird abgeleitet.** Vorher standen hier zwei
              Bedingungen mit derselben Aussage (`!text.trim()` am Mikrofon,
              `!text.trim() && fotos.length === 0` am Senden) -- die erste
              kannte die Fotos nicht: mit einem angehängten Bild und ohne Text
              zeigte die Zeile Mikrofon **und** Senden.

              Der letzte Fall deckt zugleich den Browser ohne Aufnahme ab: dort
              steht der Sendeknopf wie bisher da und ist ausgegraut. Eine leere
              rechte Seite sähe kaputt aus.

              **Mit Anhang bleibt sie trotzdem leer, und das ist kein Bruch
              mit dieser Begründung** (#63): die galt der *leeren* Zeile, in
              der es sonst gar keinen Knopf gäbe. Hängt etwas, steht der
              Sendeknopf oben an der Kachelreihe -- auf Augenhöhe mit dem, was
              er verschickt --, und das Feld bekommt den Platz zurück, den er
              darin belegt hat.

              **Und bei offener Bildschirmtastatur ebenso.** Aus dem Betrieb,
              nachdem das `+` daneben beiseitegetreten war: „das gleiche gilt
              auch für den Knopf der Sprachnachricht." Er hat dieselbe
              Schwäche -- ein Tipp nimmt dem Feld den Fokus, iOS fährt die
              Tastatur herunter, die Zeile wandert nach unten, und das
              Loslassen trifft ihn nicht mehr.

              **Hier reicht das Ausblenden per CSS aber nicht**, anders als
              beim `+`: an dieser Stelle steht *ein* Knopf, und nähme man ihn
              weg, wäre die rechte Seite leer. Also tritt der Sendeknopf an
              seine Stelle -- derselbe Zweig, der auch den Browser ohne
              Aufnahme trägt. Wer schreiben will, hat die Tastatur ohnehin
              offen; wer sprechen will, tippt neben das Feld. */}
          <span className="composer__innen">
            {!etwasZuSenden && kannAufnehmen() && !tastaturOffen ? (
              <button
                type="button"
                className="composer__innen-knopf"
                onClick={() => void aufnahme.starte()}
                aria-label={t.chat.aufnahme}
                title={t.chat.aufnahme}
              >
                <WelleIcon size={20} />
              </button>
            ) : mitAnhang ? null : (
              sendeknopf('composer__innen-knopf')
            )}
          </span>
        </div>
      </div>
      )}
    </div>
  )
}

/**
 * Eine Kachel je Foto -- mit dem Stand der Dinge darauf.
 *
 * Vorbereiten und Hochladen sind zwei verschiedene Wartezeiten: das eine
 * hängt am Telefon, das andere am Netz. Wer unterwegs steht und sich fragt,
 * ob es am Empfang liegt, sieht es hier.
 */
function Kachel({ foto, onRemove }: { foto: Attachment; onRemove: () => void }) {
  const t = useTexte()
  const laeuft = unterwegs(foto.stufe)

  return (
    <div className="attachment-item">
    <div
      className={foto.stufe === 'fehler' ? 'attachment attachment--fehler' : 'attachment'}
      title={foto.problem ?? undefined}
    >
      {foto.preview ? (
        <img src={foto.preview} alt="" className={laeuft ? 'attachment__blass' : undefined} />
      ) : (
        <span className="attachment__leer">
          {/* Ohne Standbild soll wenigstens dastehen, um was es sich
              handelt -- ein Bildsymbol an einem Video führt in die Irre. */}
          {foto.art === 'video' ? (
            <VideoIcon size={18} />
          ) : foto.art === 'ton' ? (
            <WelleIcon size={18} />
          ) : (
            <PhotoIcon size={18} />
          )}
        </span>
      )}

      {laeuft && (
        <span className="attachment__stufe">
          {/* **Auch beim Rechnen eine Zahl.** Hier stand `…` fuer alles ausser
              dem Hochladen -- bei einem Foto ist das ein Wimpernschlag, beim
              Verkleinern eines Videos steht man eine Minute davor und weiss
              nicht, ob noch etwas passiert. Die Punkte bleiben fuer den
              Moment, in dem es noch keine Zahl gibt. */}
          {foto.fortschritt > 0 && foto.stufe !== 'wartet'
            ? `${Math.round(foto.fortschritt * 100)} %`
            : '…'}
        </span>
      )}

      {/* Fertig gerechnet, aber ohne Netz: die Kachel sagt, dass sie wartet --
          sonst sähe sie aus wie eine, die durch ist. */}
      {foto.stufe === 'offline' && (
        <span className="attachment__stufe" title={t.chat.wartetAufNetz}>
          ⏳
        </span>
      )}

      {foto.stufe === 'lädt' && (
        <span className="attachment__balken">
          <span style={{ width: `${Math.round(foto.fortschritt * 100)}%` }} />
        </span>
      )}

      {foto.stufe === 'fehler' && <span className="attachment__stufe">!</span>}

      {/* Solange es läuft, gibt es nichts wegzunehmen -- der Upload wäre
          ohnehin schon unterwegs. */}
      {!laeuft && (
        <button
          type="button"
          className="attachment__remove"
          onClick={onRemove}
          aria-label={t.chat.fotoEntfernen}
          title={t.chat.entfernen}
        >
          <XIcon size={13} />
        </button>
      )}
    </div>

    {/* Was da rausgeht, in Zahlen. Bei einem verkleinerten Foto ist es eine
        Beruhigung, bei einem Video aus dem Teilen-Dialog manchmal eine
        Warnung -- und beides sieht man besser vorher als hinterher. */}
    <span
      className={
        foto.ueberGrenze ? 'attachment__groesse attachment__groesse--zuviel' : 'attachment__groesse'
      }
      title={foto.ueberGrenze ? t.chat.grenzeLiegtBei(foto.ueberGrenze) : undefined}
    >
      {/* **Die Teile werden gesammelt und dann verbunden**, statt jeder für
          sich einen Punkt mitzubringen. Vorher trug jedes Stück sein eigenes
          „ · " vor sich her, und sobald das erste wegfiel -- was seit der
          Ausbeute der Regelfall ist --, fing die Zeile mit einem Trennpunkt
          an: „· 13 kB · 360p · H.264". */}
      {[
        // Wo eine Ausbeute steht, steht die Grösse dort schon -- „2,4 MB ·
        // 0:18 · 33 MB → 2,4 MB" sagt dieselbe Zahl zweimal.
        foto.ausbeute ? '' : groesse(foto.bytes),
        (foto.art === 'video' || foto.art === 'ton')
        && foto.dauerMs !== undefined
        && foto.dauerMs > 0
          ? dauerText(foto.dauerMs)
          : '',
        // Nicht erst am Ende des Uploads: die Zahl steht ohnehin da, und
        // daneben passt der Grund, warum sie zu hoch ist.
        foto.ueberGrenze ? t.chat.ueberGrenze(foto.ueberGrenze) : '',
        // Was das Verkleinern gebracht hat, samt Auflösung und Format -- die
        // Angaben, nach denen im Betrieb gefragt wurde.
        foto.ausbeute ?? '',
      ]
        .filter((t) => t !== '')
        .join(' · ')}
    </span>

    {/* **Der Grund steht als Text da, nicht nur als `title`** (#41).
        Ein Tooltip ist auf dem Telefon unsichtbar -- das ist die Regel aus der
        Codereview, und hier wog sie neuerdings schwerer: seit ein Video, das
        sich nicht in H.264 bringen lässt, gar nicht mehr hochgeht, ist dieser
        Satz das Einzige, was den Unterschied zwischen „geht nicht" und „tut
        nichts" ausmacht. Am Kasten steht er weiterhin auch als `title` -- für
        die Maus, wo ein langer Satz sonst die Kachel breit machen würde. */}
    {foto.problem !== undefined && foto.problem !== null && (
      <span className="attachment__grund" role="alert">{foto.problem}</span>
    )}
    </div>
  )
}

/*
 * **Hier stand `isTouch()`** -- „grober Zeiger, also ein Finger, also
 * wahrscheinlich eine Kamera". Der einzige verbliebene Aufrufer war die
 * Kamerazeile im Auswahlmenü, und die ist heraus.
 *
 * Die Erfahrung dahinter bleibt aufgehoben, denn sie war teuer: das Merkmal
 * stand einmal an der Eingabetaste und hat dort den Fall verfehlt, aus dem
 * die Meldung kam -- ein iPad mit angeschlossener Tastatur meldet einen
 * groben Zeiger. **Ein Merkmal des Bildschirms kann nicht beantworten, womit
 * gerade geschrieben wird.**
 */
